import eldgardMetadata from './content/eldgard.json';

export const SITE_URL = 'https://alxnsv.io';

export type RouteMeta = {
  noindex?: boolean;
  ogType?: string;
  title: string;
  description: string;
  canonical: string;
  image?: string;
  imageWidth?: string;
  imageHeight?: string;
  imageAlt?: string;
  siteName: string;
  themeColor: string;
  icon: string;
  iconType: string;
  touchIcon: string;
};

const STUDIO_IDENTITY = {
  siteName: 'alxnsv.io',
  themeColor: '#f6f2e9',
  icon: '/brand/alxnsv-favicon.png',
  iconType: 'image/png',
  touchIcon: '/brand/alxnsv-touch-icon.png',
} as const;

const SPOOLMARK_IDENTITY = {
  siteName: 'Spoolmark',
  themeColor: '#e9eef4',
  icon: '/images/spoolmark/app-icon.png',
  iconType: 'image/png',
  touchIcon: '/images/spoolmark/app-icon.png',
} as const;

const ELDGARD_IDENTITY = {
  siteName: eldgardMetadata.siteName,
  themeColor: eldgardMetadata.themeColor,
  icon: eldgardMetadata.icon,
  iconType: eldgardMetadata.iconType,
  touchIcon: eldgardMetadata.touchIcon,
} as const;

export const routeMetadata: Record<string, RouteMeta> = {
  '/eldgard': eldgardMetadata,
  '/eldgard/docs': {
    ...ELDGARD_IDENTITY,
    title: 'Eldgard Documents — Privacy, Terms, Support, and Data',
    description:
      'Official privacy, terms, support, and data-deletion information for the Eldgard desktop workspace and mobile reader.',
    canonical: '/eldgard/docs',
  },
  '/eldgard/privacy': {
    ...ELDGARD_IDENTITY,
    title: 'Eldgard Privacy Policy',
    description:
      'How Eldgard accesses Google user data, connects to Firestore, stores local app data, and keeps users in control.',
    canonical: '/eldgard/privacy',
  },
  '/eldgard/terms': {
    ...ELDGARD_IDENTITY,
    title: 'Eldgard Terms of Use',
    description:
      'The license, responsible-use rules, product limitations, and current pre-release terms for Eldgard.',
    canonical: '/eldgard/terms',
  },
  '/eldgard/support': {
    ...ELDGARD_IDENTITY,
    title: 'Eldgard Support',
    description:
      'Get help with Eldgard, Google sign-in, IAM permissions, Firestore emulators, imports, exports, and mobile access.',
    canonical: '/eldgard/support',
  },
  '/eldgard/data-deletion': {
    ...ELDGARD_IDENTITY,
    title: 'Eldgard Data & Deletion',
    description:
      'How to disconnect Google, remove Eldgard app data, revoke service-account keys, and delete support correspondence.',
    canonical: '/eldgard/data-deletion',
  },
  '/': {
    ...STUDIO_IDENTITY,
    title: 'alxnsv.io — Independent Apps for 3D Printing & Firestore',
    description:
      'Explore Spoolmark for 3D-printing filament inventory and Eldgard for Firestore queries, typed edits, and local workflows. Independent software by Alex Nosov.',
    canonical: '/',
    image: '/og.png',
    imageWidth: '1200',
    imageHeight: '630',
  },
  '/spoolmark': {
    ...SPOOLMARK_IDENTITY,
    title: 'Spoolmark — Filament Inventory & QR Labels for 3D Printing',
    description:
      'Track 3D-printing filament, remaining weight, and print history. Create offline QR and PDF spool labels with Spoolmark. Android available; iPhone coming soon.',
    canonical: '/spoolmark',
  },
  '/spoolmark/blog': {
    ...SPOOLMARK_IDENTITY,
    title: 'Filament Tracking, NFC & RFID Notes — Spoolmark Journal',
    description:
      'Practical notes about NFC, filament identity, platform limits, and building Spoolmark with care.',
    canonical: '/spoolmark/blog',
  },
  '/spoolmark/privacy': {
    ...SPOOLMARK_IDENTITY,
    title: 'Spoolmark Privacy Policy',
    description:
      'How Spoolmark handles local inventory, NFC tags, camera intake, Bluetooth, backups, support correspondence, retention, and deletion.',
    canonical: '/spoolmark/privacy',
  },
  '/spoolmark/docs': {
    ...SPOOLMARK_IDENTITY,
    title: 'Spoolmark Documents — Privacy, Terms, Support, and Data',
    description:
      'Official Spoolmark product policies and support information for Android, iPhone, and iPad.',
    canonical: '/spoolmark/docs',
  },
  '/spoolmark/terms': {
    ...SPOOLMARK_IDENTITY,
    title: 'Spoolmark Terms of Use',
    description:
      'The license, responsible-use rules, product limitations, and platform terms for Spoolmark.',
    canonical: '/spoolmark/terms',
  },
  '/spoolmark/support': {
    ...SPOOLMARK_IDENTITY,
    title: 'Spoolmark Support',
    description:
      'Contact Spoolmark support and find help for scanning, camera intake, the optional Bluetooth reader, inventory, and store questions.',
    canonical: '/spoolmark/support',
  },
  '/spoolmark/data-deletion': {
    ...SPOOLMARK_IDENTITY,
    title: 'Spoolmark Data & Deletion',
    description:
      'How to remove local Spoolmark inventory, history, backups, physical-tag data, and support correspondence.',
    canonical: '/spoolmark/data-deletion',
  },
  '/spoolmark/blog/bambu-lab-spool-scanning-on-ios': {
    ogType: 'article',
    ...SPOOLMARK_IDENTITY,
    title: 'How I Made Bambu Lab Spool Scanning Work on iPhone — Spoolmark',
    description:
      'An iPhone cannot read original Bambu Lab RFID tags directly. Learn the practical camera and Bluetooth-reader options used by Spoolmark.',
    canonical: '/spoolmark/blog/bambu-lab-spool-scanning-on-ios',
  },
};

export const routeAliases: Record<string, string> = {
  '/spoolmark/journal': '/spoolmark/blog',
};

export function normalizePath(pathname: string) {
  if (pathname === '/') return '/';
  return pathname.replace(/\/+$/, '');
}

export function getRouteMetadata(path: string): RouteMeta {
  return (
    routeMetadata[routeAliases[path] ?? path] ?? {
      ...STUDIO_IDENTITY,
      title: 'Page not found — alxnsv.io',
      description: 'The page you were looking for could not be found.',
      canonical: '/404',
      noindex: true,
    }
  );
}

export function getMetaTags(page: RouteMeta) {
  const image = page.image ? `${SITE_URL}${page.image}` : undefined;
  return {
    name: {
      description: page.description,
      'theme-color': page.themeColor,
      robots: page.noindex ? 'noindex, follow' : undefined,
      'twitter:card': image ? 'summary_large_image' : 'summary',
      'twitter:title': page.title,
      'twitter:description': page.description,
      'twitter:image': image,
      'twitter:image:alt': page.imageAlt,
    },
    property: {
      'og:type': page.ogType ?? 'website',
      'og:site_name': page.siteName,
      'og:title': page.title,
      'og:description': page.description,
      'og:url': page.noindex ? undefined : `${SITE_URL}${page.canonical}`,
      'og:image': image,
      'og:image:width': page.imageWidth,
      'og:image:height': page.imageHeight,
      'og:image:alt': page.imageAlt,
    },
  };
}

export function getStructuredData(page: RouteMeta) {
  if (page.noindex) return undefined;
  const organization = {
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: 'alxnsv.io',
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/brand/alxnsv-touch-icon.png`,
  };
  const website = {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: 'alxnsv.io',
    url: `${SITE_URL}/`,
    publisher: { '@id': organization['@id'] },
  };
  if (page.canonical === '/') {
    return {
      '@context': 'https://schema.org',
      '@graph': [organization, website],
    };
  }
  return {
    '@context': 'https://schema.org',
    '@type': page.ogType === 'article' ? 'BlogPosting' : 'WebPage',
    url: `${SITE_URL}${page.canonical}`,
    name: page.title,
    description: page.description,
    isPartOf: { '@id': website['@id'] },
    ...(page.ogType === 'article'
      ? {
          headline: 'How I Made Bambu Lab Spool Scanning Work on iPhone',
          author: {
            '@type': 'Person',
            name: 'Alex Nosov',
            url: `${SITE_URL}/#about`,
          },
          mainEntityOfPage: `${SITE_URL}${page.canonical}`,
        }
      : {}),
  };
}
